<template>
    <v-app v-cloak>
        <div class="widget-component">
        <v-container fluid id="widget-forgot-password-frame" class="widget-forgot-password">
            <v-container class="container-width">
                <v-row>
                    <v-col cols="12">
                        <v-card
                            :loading="loading"
                            class="mx-auto my-12"
                            :width="normalizedOptions.card.width"
                            :elevation="normalizedOptions.card.elevation"
                        >
                            <v-card-text>
                                <v-row
                                    align="center"
                                    class="mx-0"
                                >
                                    <v-col cols="12" class="px-lg-16 py-lg-16">
                                        <v-col cols="12">
                                            <h1 class="mb-6 color-primary">{{ normalizedOptions.title }}</h1>
                                        </v-col>

                                        <v-col cols="12" v-if="normalizedOptions.intro.show">
                                            <div v-html="normalizedOptions.intro.textHtml"></div>
                                        </v-col>

                                        <v-col cols="12">
                                            <label
                                                for="emailField"
                                                v-if="!normalizedOptions.fields.email.inlineLabels"
                                                class="mb-4 font-weight-bold"
                                            >
                                                {{ normalizedOptions.fields.email.label }}
                                            </label>
                                            <v-text-field
                                                id="emailField"
                                                autofocus
                                                v-model="email"
                                                :label="normalizedOptions.fields.email.inlineLabels ? normalizedOptions.fields.email.label : ''"
                                                outlined
                                                hide-details="auto"
                                                class="rounded-0 mb-4"
                                                required
                                                :error-messages="getError('email')"
                                            ></v-text-field>
                                        </v-col>

                                        <v-col cols="12" md="8">
                                            <v-btn
                                                color="primary"
                                                @click="submit"
                                                class="ma-0"
                                                large
                                                block
                                                :loading="loading"
                                                :disabled="loading"
                                            >
                                                {{ normalizedOptions.fields.submitButton.text }}
                                            </v-btn>
                                        </v-col>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>
        <v-dialog
            max-width="400"
            v-model="dialog"
        >
            <template v-slot:default="dialog">
                <v-card>
                    <v-toolbar
                        color="primary"
                        v-if="normalizedOptions.dialog.success.toolbar.show"
                    >
                        <span :style="{ 'color': normalizedOptions.dialog.success.toolbar.textColour }">
                            {{ normalizedOptions.dialog.success.toolbar.text }}
                        </span>
                    </v-toolbar>
                    <v-card-text>
                        <div class="pa-4">
                            <div v-html="normalizedOptions.dialog.success.textHtml"></div>
                        </div>
                    </v-card-text>
                    <v-card-actions class="justify-end">
                        <div v-if="normalizedOptions.dialog.success.buttons.close.show">
                            <v-btn
                                v-if="normalizedOptions.dialog.success.buttons.close.url == ''"
                                color="primary"
                                @click="closeDialog(true)"
                            >
                                {{ normalizedOptions.dialog.success.buttons.close.text }}
                            </v-btn>
                            <v-btn
                                v-else
                                color="primary"
                                link
                                :href="normalizedOptions.dialog.success.buttons.close.url"
                            >
                                {{ normalizedOptions.dialog.success.buttons.close.text }}
                            </v-btn>
                        </div>
                    </v-card-actions>
                </v-card>
            </template>
        </v-dialog>
        </div>
    </v-app>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from "@vue/composition-api";
import {has, merge, replace} from "lodash";
import AuthenticationService from "../services/AuthenticationService";
export default defineComponent({
    name: "ForgotPassword",
    components: {  },
    props: {
        rootUrl: {
            type: String,
            required: true
        },
        settings: {
            type: Object
        },
        accessToken: {
            type: String
        },
    },
    setup(props) {
        const loading = ref(false);
        const email = ref(null);
        const errors = ref(null);
        const dialog = ref(null);

        const defaultSettings = ref({
            title: 'Forgot password?',
            passwordFormUrl: getDefaultPasswordFormUrl(),
            intro: {
                show: true,
                textHtml: '<p>Enter your email address, and we will send you a link to reset your password.</p>'
            },
            card: {
                width: 622,
                elevation: 5
            },
            fields: {
                email: {
                    inlineLabels: false,
                    label: 'E-mail*'
                },
                submitButton: {
                    text: 'Submit'
                }
            },
            dialog: {
                success: {
                    textHtml: '<p>We have sent you an email with a link to reset your password.<br />' +
                        'If you do not see our email in your inbox, please check your spam folder.</p>',
                    toolbar: {
                        show: true,
                        text: 'Success',
                        textColour: 'white'
                    },
                    buttons: {
                        close: {
                            show: true,
                            text: 'Close',
                            url: "/login"
                        }
                    }
                }
            },
            events: {
            }
        });

        const normalizedOptions = computed(() => {
            return merge(defaultSettings.value, props.settings);
        });

        function openDialog() {
            dialog.value = true;
        }

        function closeDialog(clearEmail = false) {
            if(clearEmail) {
                email.value = null;
            }
            dialog.value = false;
        }

        function getError(field) {
            const fieldToSearch = 'errors.' + field + '[0]';
            if(has(errors.value, fieldToSearch)) {
                return errors.value.errors[field][0];
            } else if (has(errors.value, 'message')) {
                return errors.value.message;
            }
            return null
        }

        function submit() {
            loading.value = true;
            errors.value = null;
            AuthenticationService.sendResetEmail(email.value, normalizedOptions.value.passwordFormUrl)
                .then((response) => {
                    if(has(response, 'status')) {
                        if(response.status === 200) {
                            openDialog();
                        }
                    }
                })
                .catch(error => {
                    console.error(error);
                    if(has(error, 'response.data')) {
                        errors.value = error.response.data;
                    }
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        function getDefaultPasswordFormUrl() {
            let url = document.location.origin + '/forgot-password/';
            if(has(window, 'kproDataLayer.settings.urls.forgotPasswordChange')) {
                url = replace(window.kproDataLayer.settings.urls.forgotPasswordChange, '{id}', '');
                url = replace(url, '{token}', '');
                if(url.slice(-2) == '//') {
                    url = url.slice(0, -1);
                }
            } else if (has(props, 'settings.passwordFormUrl')) {
                url = props.settings.passwordFormUrl;
            }
            return url;
        }

        onMounted(() => {
            init();
        });

        function init() {
            loading.value = false;
        }

        return {
            // Variables
            normalizedOptions,
            loading,
            email,
            errors,
            dialog,
            // Functions
            submit,
            getError,
            openDialog,
            closeDialog,
        };
    }
});
</script>
